import { Controller } from "@hotwired/stimulus"
import { track } from "../lib/mixpanel"

// AI Smart Import (Phelipe 2026-09-21) — Super Admin tool on Settings →
// Properties and on All Phases. One file, two roles: the purple button is a
// "launcher" instance that only asks for the pop-up; the pop-up instance at
// the foot of the page does everything else.
//
//   upload → analyzing → review → importing → done
//
// Nothing is written before "Confirm import". The review survives closing
// the pop-up (an accidental backdrop click must not cost a 600-row review),
// and the import itself runs in short server chunks so it shows real
// progress and never times out.
const PAGE = 50

const TABS = [
  { key: "possible", label: "Possible duplicates" },
  { key: "new", label: "New" },
  { key: "matched", label: "Already in ConsLog" },
  { key: "invalid", label: "Can't import" }
]

const CHIPS = {
  high: ["High-confidence match", "bg-green-100 text-green-700"],
  possible: ["Possible match", "bg-amber-100 text-amber-700"],
  none: ["No match", "bg-gray-100 text-gray-600"]
}

const SPINNER = `<svg class="animate-spin w-5 h-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>`

export default class extends Controller {
  static targets = ["panel", "body", "footer", "fileInput"]
  static values = { launcher: Boolean, kind: String, url: String, phases: Array, company: String }

  connect() {
    if (this.launcherValue) return
    this._reset()
  }

  disconnect() {
    clearInterval(this._ticker)
  }

  // ── Launcher role ───────────────────────────────────────────────────

  launch() {
    window.dispatchEvent(new CustomEvent("smart-import:open", { detail: { kind: this.kindValue } }))
  }

  // The clock next to the button: straight to past imports and their revert.
  launchHistory() {
    window.dispatchEvent(new CustomEvent("smart-import:open", { detail: { kind: this.kindValue, history: true } }))
  }

  // ── Open / close ────────────────────────────────────────────────────

  open(event) {
    if (this.launcherValue || event.detail?.kind !== this.kindValue) return
    this.element.classList.remove("hidden")
    // A review or a run in progress is never thrown away by the clock.
    if (event.detail?.history && ["upload", "history"].includes(this.step)) {
      track("Smart Import History Opened", { import_kind: this.kindValue })
      return this.showHistory()
    }
    if (this.step === "upload") track("Smart Import Opened", { import_kind: this.kindValue })
    this._render()
  }

  hideModal() {
    if (this.launcherValue || this.step === "importing") return
    // After an undo the page behind is out of date, however the pop-up closes.
    if (this.step === "done" || this.reverted) return this.finish()
    this.element.classList.add("hidden")
  }

  escape() {
    if (!this.launcherValue && !this.element.classList.contains("hidden")) this.hideModal()
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  startOver() {
    this._reset()
    this._render()
  }

  finish() {
    const imported = this.step === "done" && this.progress?.result &&
      (this.progress.result.created > 0 || this.progress.result.filled > 0)
    // Properties already reloaded the moment the undo finished.
    const reverted = this.reverted && !this.listRefreshed
    this.element.classList.add("hidden")
    this._reset()
    if (!imported && !reverted) return

    if (imported) this._showToast("Import completed")
    if (this.kindValue === "projects" && window.Turbo) {
      window.Turbo.visit(window.location.href, { action: "replace" })
    } else {
      window.dispatchEvent(new CustomEvent("smart-import:done", { detail: { kind: this.kindValue } }))
    }
  }

  _reset() {
    clearInterval(this._ticker)
    this.step = "upload"
    this.file = null
    this.review = null
    this.decisions = {}
    this.progress = null
    this.error = null
    this.tab = "new"
    this.page = 0
    this.filter = ""
    this.history = null
    this.confirmRevert = null
    this.reverting = false
    this.reverted = false
    this.listRefreshed = false
    this.phase = this.phasesValue[0]?.[1] || "construction"
    if (this.hasFileInputTarget) this.fileInputTarget.value = ""
  }

  // ── Step 1: upload ──────────────────────────────────────────────────

  pickFile() {
    this.fileInputTarget.click()
  }

  fileChosen() {
    this._takeFile(this.fileInputTarget.files[0])
  }

  removeFile() {
    this.file = null
    this.fileInputTarget.value = ""
    this._render()
  }

  dragOver(event) {
    event.preventDefault()
    event.currentTarget.classList.add("border-blue-400", "bg-blue-50")
  }

  dragLeave(event) {
    event.currentTarget.classList.remove("border-blue-400", "bg-blue-50")
  }

  drop(event) {
    event.preventDefault()
    event.currentTarget.classList.remove("border-blue-400", "bg-blue-50")
    this._takeFile(event.dataTransfer?.files?.[0])
  }

  setPhase(event) {
    this.phase = event.target.value
  }

  _takeFile(file) {
    if (!file) return
    this.error = /\.(xlsx|xlsm|xls|csv)$/i.test(file.name) ? null : "That file type isn't supported — use .xlsx, .xls or .csv."
    this.file = this.error ? null : file
    this._render()
  }

  // ── Step 2: analyze ─────────────────────────────────────────────────

  analyze() {
    this._analyze({})
  }

  // Re-run with the admin's own column picks from the mapping panel.
  remap() {
    const columns = {}
    this.bodyTarget.querySelectorAll("[data-map-field]").forEach((select) => {
      if (select.value === "") return
      const key = select.dataset.mapField
      ;(columns[key] ||= []).push(parseInt(select.value, 10))
    })
    const sheet = this.bodyTarget.querySelector("[data-map-sheet]")?.value || this.review.mapping.sheet
    const sameSheet = sheet === this.review.mapping.sheet
    // A different sheet has different columns: let the AI read that one afresh.
    this._analyze(sameSheet ? { sheet, header_row: this.review.mapping.header_row, columns: JSON.stringify(columns) } : { sheet })
  }

  async _analyze(overrides) {
    if (!this.file) return
    this.step = "analyzing"
    this.error = null
    this._render()

    const form = new FormData()
    form.append("file", this.file)
    form.append("kind", this.kindValue)
    form.append("phase", this.phase)
    Object.entries(overrides).forEach(([key, value]) => form.append(key, value))

    try {
      const response = await fetch(this.urlValue, { method: "POST", headers: this._headers(), body: form })
      const data = await response.json().catch(() => ({}))
      if (!response.ok) throw new Error(data.error || "The file couldn't be analyzed. Try again.")

      // Not `this.data`: that name is a read-only getter on every Stimulus controller.
      this.review = data
      this.decisions = {}
      data.rows.forEach((row) => { this.decisions[row.i] = row.action })
      this.tab = TABS.find((t) => this._rowsIn(t.key).length > 0)?.key || "new"
      this.page = 0
      this.filter = ""
      this.step = "review"
    } catch (error) {
      this.error = error.message
      this.step = "upload"
    }
    this._render()
  }

  // ── Step 3: review ──────────────────────────────────────────────────

  setTab(event) {
    this.tab = event.currentTarget.dataset.tab
    this.page = 0
    this.filter = ""
    this._render()
  }

  prevPage() {
    this.page = Math.max(0, this.page - 1)
    this._renderReviewTable()
  }

  nextPage() {
    this.page += 1
    this._renderReviewTable()
  }

  filterRows(event) {
    this.filter = event.target.value
    this.page = 0
    this._renderReviewTable()
  }

  toggleRow(event) {
    this.decisions[event.target.dataset.row] = event.target.checked ? "create" : "skip"
    this._renderFooter()
  }

  setDecision(event) {
    this.decisions[event.target.dataset.row] = event.target.value
    this._renderFooter()
  }

  // "Import all" / "Skip all" for whatever the current tab + search shows.
  bulk(event) {
    const action = event.currentTarget.dataset.bulk
    this._visibleRows().forEach((row) => {
      if (this._allowed(row).includes(action)) this.decisions[row.i] = action
    })
    this._renderReviewTable()
    this._renderFooter()
  }

  _allowed(row) {
    if (row.bucket === "new") return ["create", "skip"]
    if (row.bucket === "possible") return row.match ? ["skip", "create", "fill"] : ["skip", "create"]
    if (row.bucket === "matched") return ["skip", "fill"]
    return ["skip"]
  }

  _rowsIn(tab) {
    return this.review.rows.filter((row) => row.bucket === tab)
  }

  _visibleRows() {
    const needle = this.filter.trim().toLowerCase()
    const rows = this._rowsIn(this.tab)
    if (!needle) return rows
    return rows.filter((row) =>
      [...Object.values(row.values), row.match?.label, row.match?.detail, row.reason, String(row.line)]
        .some((text) => String(text || "").toLowerCase().includes(needle)))
  }

  _counts() {
    const counts = { create: 0, fill: 0, skip: 0 }
    this.review.rows.forEach((row) => { counts[this.decisions[row.i] || "skip"] += 1 })
    return counts
  }

  // ── Step 4: import ──────────────────────────────────────────────────

  async confirmImport() {
    const counts = this._counts()
    if (counts.create + counts.fill === 0) return

    this.step = "importing"
    this.error = null
    this.progress = { done: 0, total: counts.create + counts.fill }
    this._render()

    try {
      this.progress = await this._post(`${this.urlValue}/${this.review.id}/confirm`, { decisions: this.decisions, phase: this.phase })
      await this._runLoop()
    } catch (error) {
      // Refused before anything was written (plan limit, already confirmed):
      // back to the review with the reason.
      this.error = error.message
      this.step = this.progress?.status === "importing" ? "importing" : "review"
      this._render()
    }
  }

  // Safe to call again after a network blip: the server skips rows it has
  // already applied.
  async retryRun() {
    this.error = null
    this._render()
    try {
      await this._runLoop()
    } catch (error) {
      this.error = error.message
      this._render()
    }
  }

  async _runLoop() {
    while (!this.progress.finished) {
      this.progress = await this._post(`${this.urlValue}/${this.review.id}/run`, {})
      this._render()
      // A breath between steps so other pages (and other tabs) get the server too.
      if (!this.progress.finished) await new Promise((resolve) => setTimeout(resolve, 250))
    }
    this.step = "done"
    this._render()
  }

  // ── History + revert (Phelipe 2026-10-06) ─────────────────────────────

  showHistory() {
    this.step = "history"
    this.history = null
    this.confirmRevert = null
    this.error = null
    this._render()
    this._loadHistory()
  }

  backToUpload() {
    this.step = "upload"
    this.error = null
    this._render()
  }

  async _loadHistory() {
    try {
      const response = await fetch(`${this.urlValue}?kind=${encodeURIComponent(this.kindValue)}`, { headers: this._headers() })
      const data = await response.json().catch(() => ({}))
      if (!response.ok) throw new Error(data.error || "Couldn't load the past imports.")
      this.history = data.imports || []
    } catch (error) {
      this.history = []
      this.error = error.message
    }
    if (this.step === "history") this._render()
  }

  askRevert(event) {
    this.confirmRevert = parseInt(event.currentTarget.dataset.id, 10)
    this._render()
  }

  cancelRevert() {
    this.confirmRevert = null
    this._render()
  }

  async doRevert() {
    const id = this.confirmRevert
    if (!id || this.reverting) return
    this.reverting = true
    this._render()
    try {
      const row = await this._post(`${this.urlValue}/${id}/revert`, {})
      this.history = (this.history || []).map((h) => (h.id === row.id ? row : h))
      this.reverted = true
      // The properties list behind the pop-up reloads in place right away;
      // All Phases redraws when the pop-up closes (a page visit would close it).
      if (this.kindValue !== "projects") {
        window.dispatchEvent(new CustomEvent("smart-import:done", { detail: { kind: this.kindValue } }))
        this.listRefreshed = true
      }
      const t = row.reverted?.tally || {}
      this._showToast(`Reverted: ${t.removed || 0} removed, ${t.cleared || 0} cleared, ${t.kept || 0} kept`)
    } catch (error) {
      this.error = error.message
    } finally {
      this.reverting = false
      this.confirmRevert = null
      this._render()
    }
  }

  async _post(url, body) {
    const response = await fetch(url, {
      method: "POST",
      headers: { ...this._headers(), "Content-Type": "application/json" },
      body: JSON.stringify(body)
    })
    const data = await response.json().catch(() => ({}))
    if (!response.ok) throw new Error(data.error || "Something went wrong. Try again.")
    return data
  }

  _headers() {
    return { "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content, Accept: "application/json" }
  }

  // ── Rendering ───────────────────────────────────────────────────────

  _render() {
    if (this.launcherValue) return
    clearInterval(this._ticker)
    const wide = this.step === "review" || this.step === "history"
    this.panelTarget.classList.toggle("max-w-xl", !wide)
    this.panelTarget.classList.toggle("max-w-6xl", wide)

    if (this.step === "upload") this._renderUpload()
    else if (this.step === "analyzing") this._renderAnalyzing()
    else if (this.step === "review") this._renderReview()
    else if (this.step === "importing") this._renderImporting()
    else if (this.step === "history") this._renderHistory()
    else this._renderDone()
  }

  _renderUpload() {
    const what = this.kindValue === "projects" ? "projects" : "properties"
    const chip = this.file ? `
      <div class="mt-3 inline-flex items-center gap-2 max-w-full px-2.5 py-1.5 rounded-lg bg-gray-100 text-[13px] text-gray-700">
        <svg class="w-4 h-4 text-gray-400 shrink-0" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"/></svg>
        <span class="truncate">${this._esc(this.file.name)}</span>
        <button type="button" class="text-gray-400 hover:text-gray-600 cursor-pointer shrink-0" aria-label="Remove file" data-action="click->smart-import#removeFile">&times;</button>
      </div>` : ""

    // No phase pick here (Phelipe 2026-10-06): the file says where each
    // project goes; the review asks only for rows that carry no phase.
    const phasePicker = ""

    this.bodyTarget.innerHTML = `
      <div class="px-5 py-5">
        <p class="text-sm text-gray-600">Upload the client's spreadsheet of ${what} just as they sent it. AI works out the columns, then every row is checked against what <span class="font-medium text-gray-800">${this._esc(this.companyValue)}</span> already has in ConsLog.</p>
        <p class="text-sm text-gray-600 mt-2"><span class="font-medium text-gray-800">Nothing is imported until you review the result and confirm.</span></p>

        <div class="mt-4 rounded-xl border-2 border-dashed border-gray-300 px-4 py-8 text-center cursor-pointer transition-colors hover:border-gray-400"
             data-action="click->smart-import#pickFile dragenter->smart-import#dragOver dragover->smart-import#dragOver dragleave->smart-import#dragLeave drop->smart-import#drop">
          <svg class="w-8 h-8 mx-auto text-gray-400" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5"/></svg>
          <p class="mt-2 text-sm text-gray-700">Drop the spreadsheet here, or <span class="text-brand font-medium">browse</span></p>
          <p class="text-[13px] text-gray-500 mt-0.5">.xlsx, .xls or .csv &middot; any column layout &middot; extra tabs are fine</p>
        </div>
        ${chip}
        ${phasePicker}
        ${this.error ? `<p class="mt-3 text-sm text-red-600">${this._esc(this.error)}</p>` : ""}
        <p class="mt-4 text-[13px] text-gray-500">Already imported something? <button type="button" class="text-brand font-medium hover:underline cursor-pointer" data-action="click->smart-import#showHistory">See past imports, or revert one</button></p>
      </div>`

    this.footerTarget.innerHTML = `
      <span></span>
      <div class="flex items-center gap-2">
        <button type="button" class="px-4 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer" data-action="click->smart-import#hideModal">Cancel</button>
        <button type="button" class="inline-flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium text-white bg-brand hover:bg-brand-dark rounded-lg transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                data-action="click->smart-import#analyze" ${this.file ? "" : "disabled"}>Analyze with AI</button>
      </div>`
  }

  _renderAnalyzing() {
    const lines = ["Reading the spreadsheet…", "AI is working out the columns…", "Checking every row against ConsLog…", "Getting a second opinion on the look-alikes…"]
    this.bodyTarget.innerHTML = `
      <div class="px-5 py-14 flex flex-col items-center text-center">
        <span class="text-purple-700">${SPINNER}</span>
        <p class="mt-3 text-sm font-medium text-gray-800" data-analyzing-line>${lines[0]}</p>
        <p class="mt-1 text-[13px] text-gray-500">Big files take a little longer. Nothing is being imported yet.</p>
      </div>`
    this.footerTarget.innerHTML = `<span></span><span></span>`
    let index = 0
    this._ticker = setInterval(() => {
      index = Math.min(index + 1, lines.length - 1)
      const line = this.bodyTarget.querySelector("[data-analyzing-line]")
      if (line) line.textContent = lines[index]
    }, 2200)
  }

  _renderReview() {
    const { mapping, summary } = this.review
    const readBy = { ai: "Columns read by AI", rules: "Columns read by built-in rules (AI was unavailable)", manual: "Columns set by you" }[mapping.source] || ""

    this.bodyTarget.innerHTML = `
      <div class="px-5 pt-4 pb-2">
        <div class="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
          <p class="text-[13px] text-gray-600 min-w-0 truncate">
            <span class="font-medium text-gray-800">${this._esc(this.review.filename)}</span>
            &middot; sheet “${this._esc(mapping.sheet)}” &middot; ${summary.total} rows &middot; ${readBy}
          </p>
          <button type="button" class="text-[13px] text-brand hover:underline cursor-pointer shrink-0" data-action="click->smart-import#startOver">Start over with another file</button>
        </div>
        ${this._mappingPanel()}
        ${this._banners()}
        ${this.error ? `<div class="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">${this._esc(this.error)}</div>` : ""}

        <div class="mt-4 flex flex-wrap items-center gap-1 border-b border-gray-200">
          ${TABS.map((tab) => {
            const count = this._rowsIn(tab.key).length
            const active = tab.key === this.tab
            return `<button type="button" data-tab="${tab.key}" data-action="click->smart-import#setTab"
                      class="px-3 py-2 text-sm -mb-px border-b-2 cursor-pointer transition-colors ${active ? "border-brand text-brand font-semibold" : "border-transparent text-gray-500 hover:text-gray-700"}">
                      ${tab.label} <span class="ml-1 inline-flex items-center justify-center min-w-[20px] px-1.5 rounded-full text-[13px] ${active ? "bg-brand text-white" : "bg-gray-100 text-gray-600"}">${count}</span>
                    </button>`
          }).join("")}
        </div>
      </div>
      <div class="px-5 pb-4" data-review-table></div>`

    this._renderReviewTable()
    this._renderFooter()
  }

  _mappingPanel() {
    const { mapping } = this.review
    const options = (selected) => `<option value="">— not in this file —</option>` +
      mapping.headers.map((header, index) => header === "" ? "" :
        `<option value="${index}" ${index === selected ? "selected" : ""}>${this._esc(header)}</option>`).join("")

    const fields = this.review.fields.map((field) => {
      const picked = mapping.columns[field.key] || []
      const selects = (field.multi ? [0, 1] : [0]).map((slot) => `
        <select data-map-field="${field.key}" class="w-full rounded-lg border border-gray-300 bg-white text-[13px] px-2 py-1">
          ${options(picked[slot])}
        </select>`).join(`<span class="text-[13px] text-gray-400 text-center">+</span>`)
      return `
        <div class="min-w-0">
          <label class="block text-[13px] font-medium text-gray-700 mb-0.5">${this._esc(field.label)}${field.required ? ` <span class="text-red-500">*</span>` : ""}</label>
          <div class="${field.multi ? "grid grid-cols-[1fr_auto_1fr] items-center gap-1" : ""}">${selects}</div>
        </div>`
    }).join("")

    const sheetPicker = mapping.sheets.length > 1 ? `
      <div class="min-w-0">
        <label class="block text-[13px] font-medium text-gray-700 mb-0.5">Sheet</label>
        <select data-map-sheet class="w-full rounded-lg border border-gray-300 bg-white text-[13px] px-2 py-1">
          ${mapping.sheets.map((name) => `<option value="${this._esc(name)}" ${name === mapping.sheet ? "selected" : ""}>${this._esc(name)}</option>`).join("")}
        </select>
      </div>` : ""

    return `
      <details class="mt-3 rounded-lg border border-gray-200 bg-gray-50">
        <summary class="px-3 py-2 text-[13px] font-medium text-gray-700 cursor-pointer select-none">How the file was read — check the columns, fix anything that's off</summary>
        <div class="px-3 pb-3">
          ${mapping.notes ? `<p class="text-[13px] text-purple-700 mb-2">AI note: ${this._esc(mapping.notes)}</p>` : ""}
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-3 gap-y-2">${sheetPicker}${fields}</div>
          <div class="mt-3 flex justify-end">
            <button type="button" class="px-3 py-1 text-[13px] font-medium text-purple-700 bg-purple-100 hover:bg-purple-200 rounded-lg cursor-pointer transition-colors" data-action="click->smart-import#remap">Re-check with these columns</button>
          </div>
        </div>
      </details>`
  }

  _banners() {
    const { summary } = this.review
    const banners = []
    const amber = (text) => `<div class="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[13px] text-amber-800">${text}</div>`
    const blue = (text) => `<div class="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-[13px] text-blue-800">${text}</div>`

    if (summary.truncated) banners.push(amber("Only the first 5,000 rows were read. Split the file to bring in the rest."))
    if (summary.in_file_duplicates > 0) banners.push(amber(`${summary.in_file_duplicates} row(s) repeat an earlier row of this same file — they're under Possible duplicates and won't be imported unless you say so.`))
    if (summary.adds_property > 0) banners.push(blue(`${summary.adds_property} project(s) already in ConsLog have no property yet — they're set to get theirs from this file (Fill in its missing info). Nothing they already have is changed.`))
    if (summary.unlinked > 0) banners.push(amber(`${summary.unlinked} project(s) point to a property that isn't in ConsLog yet. Import the properties first (Settings → Properties → AI Smart Import) and re-run this file to link them — or carry on and they'll be created without a property.`))
    if (summary.billing) banners.push(blue("This company is on a paid subscription — every new project is added to its billing."))
    if (this.kindValue === "projects") {
      const labels = { real_estate: "Real Estate", dev: "Design", bidding: "Bidding", construction: "Construction", operations: "Operations" }
      const picker = this.phasesValue.length > 1
        ? `<select class="rounded-lg border border-blue-300 bg-white text-[13px] px-2 py-0.5 mx-1" data-action="change->smart-import#setPhase">
             ${this.phasesValue.map(([label, value]) => `<option value="${this._esc(value)}" ${value === this.phase ? "selected" : ""}>${this._esc(label)}</option>`).join("")}
           </select>`
        : `<span class="font-medium">${this._esc(this.phasesValue[0]?.[0] || "Construction")}</span>`
      if (summary.phases) {
        const parts = Object.entries(summary.phases).filter(([k]) => k !== "none").map(([k, n]) => `${n} ${labels[k] || k}`)
        const none = summary.phases.none ? ` ${summary.phases.none} row(s) carry no phase — they go to ${picker}.` : ""
        banners.push(blue(`Phases come from the file: ${parts.map((p) => this._esc(p)).join(" · ")}. Each project also lands in the company's step named in the file, when there is one.${none}`))
      } else if (summary.new > 0 || summary.possible > 0) {
        banners.push(blue(`No phase column was found, so new projects go to ${picker}.`))
      }
    }

    const extras = [["new_regions", "region"], ["new_affiliates", "entity", "entities"], ["new_building_types", "property type"]]
      .filter(([key]) => (summary[key] || []).length > 0)
      .map(([key, one, many]) => {
        const names = summary[key]
        const shown = names.slice(0, 3).map((n) => this._esc(n)).join(", ")
        return `<span title="${this._esc(names.join(", "))}">${names.length}${names.length >= 40 ? "+" : ""} new ${names.length === 1 ? one : (many || `${one}s`)} (${shown}${names.length > 3 ? ", …" : ""})</span>`
      })
    if (extras.length) banners.push(blue(`Importing will also add ${extras.join(" · ")}. If that's not what those columns mean, change them under “How the file was read”.`))

    return banners.length ? `<div class="mt-3 space-y-2">${banners.join("")}</div>` : ""
  }

  _renderReviewTable() {
    const holder = this.bodyTarget.querySelector("[data-review-table]")
    if (!holder) return

    const all = this._rowsIn(this.tab)
    const rows = this._visibleRows()
    const pages = Math.max(1, Math.ceil(rows.length / PAGE))
    this.page = Math.min(this.page, pages - 1)
    const slice = rows.slice(this.page * PAGE, (this.page + 1) * PAGE)

    const hints = {
      possible: this.kindValue === "projects"
        ? "These look like something already in ConsLog, or repeat a row of this file. A project is only a sure match when number, name and phase all agree — the same number in another phase, or a second row for a project another line already fills in, is set to be created as a new project; switch it to fill in if it's really the same one."
        : "These look like something already in ConsLog, or repeat a row of this file. Nothing here is imported unless you choose to.",
      new: "No match found in ConsLog. Ticked rows will be created.",
      matched: this.kindValue === "projects"
        ? "Already in ConsLog, so they're skipped — except ones missing their property, which get it from this file. You can fill in other details ConsLog is missing — nothing existing is ever overwritten."
        : "Already in ConsLog, so they're skipped. You can fill in details ConsLog is missing — nothing existing is ever overwritten.",
      invalid: "These rows can't be imported as they are."
    }
    const bulk = { new: [["create", "Tick all"], ["skip", "Untick all"]], possible: [["create", "Import all as new"], ["skip", "Skip all"]], matched: [["fill", "Fill in missing info on all"], ["skip", "Skip all"]] }[this.tab] || []

    if (all.length === 0) {
      holder.innerHTML = `<p class="py-10 text-center text-sm text-gray-500">Nothing here.</p>`
      return
    }

    holder.innerHTML = `
      <div class="flex flex-wrap items-center justify-between gap-2 py-2.5">
        <p class="text-[13px] text-gray-500">${hints[this.tab]}</p>
        <div class="flex items-center gap-2">
          ${bulk.map(([action, label]) => `<button type="button" data-bulk="${action}" data-action="click->smart-import#bulk" class="px-2.5 py-1 text-[13px] font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors whitespace-nowrap">${label}</button>`).join("")}
          <input type="text" value="${this._esc(this.filter)}" placeholder="Search these rows…" data-action="input->smart-import#filterRows"
                 class="w-44 rounded-lg border border-gray-300 bg-white text-[13px] px-2.5 py-1 focus:outline-none focus:ring-1 focus:ring-brand">
        </div>
      </div>
      <div class="border border-gray-200 rounded-xl overflow-hidden overflow-x-auto">
        <table class="w-full text-[13px]">
          <thead class="bg-gray-50 text-gray-500 font-medium">
            <tr>
              <th class="px-2 py-1.5 text-left w-14">Line</th>
              <th class="px-2 py-1.5 text-left">In the spreadsheet</th>
              <th class="px-2 py-1.5 text-left">In ConsLog</th>
              <th class="px-2 py-1.5 text-left w-64">Decision</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200">
            ${slice.map((row) => this._rowHtml(row)).join("") || `<tr><td colspan="4" class="px-2 py-6 text-center text-gray-500">No rows match that search.</td></tr>`}
          </tbody>
        </table>
      </div>
      ${rows.length > PAGE ? `
        <div class="flex items-center justify-end gap-2 pt-2 text-[13px] text-gray-600">
          <span>${this.page * PAGE + 1}–${Math.min((this.page + 1) * PAGE, rows.length)} of ${rows.length}</span>
          <button type="button" class="px-2 py-0.5 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed" data-action="click->smart-import#prevPage" ${this.page === 0 ? "disabled" : ""}>Prev</button>
          <button type="button" class="px-2 py-0.5 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed" data-action="click->smart-import#nextPage" ${this.page >= pages - 1 ? "disabled" : ""}>Next</button>
        </div>` : ""}`

    // Typing in the search box re-renders the table; keep the caret there.
    if (this.filter) {
      const input = holder.querySelector("input[type=text]")
      input?.focus()
      input?.setSelectionRange(this.filter.length, this.filter.length)
    }
  }

  _rowHtml(row) {
    const v = row.values
    const projects = this.kindValue === "projects"
    const title = projects ? [v.number, v.name].filter(Boolean).join(" — ") : [v.code, v.name].filter(Boolean).join(" — ")
    const detail = projects
      ? [v.phase_label ? `${v.phase_label}${v.secondary_phase_label ? ` · ${v.secondary_phase_label}` : (v.secondary_phase ? ` · ${v.secondary_phase} (step not set up)` : "")}` : (v.secondary_phase ? `Step ${v.secondary_phase}` : ""),
         v.completed ? "Completed" : "",
         v.property_label ? `Property ${v.property_label}` : (v.property_code ? `Property ${v.property_code}` : ""), v.kind,
         [v.address, v.city, v.state].filter(Boolean).join(", "), v.project_manager ? `PM ${v.project_manager}` : "",
         v.started_at || v.completed_at ? `${v.started_at || "?"} → ${v.completed_at || "?"}` : "",
         v.amount ? `Contract ${this._money(v.amount)}` : ""]
      : [[v.address, v.city, v.state, v.zipcode, v.country].filter(Boolean).join(", "), v.region, v.affiliate, v.building_type]

    const [chipLabel, chipClass] = CHIPS[row.confidence] || CHIPS.none
    let match = `<span class="inline-block px-2 py-0.5 rounded-full ${chipClass}">${chipLabel}</span>`
    if (row.match) {
      const open = projects && Number.isInteger(row.match.id)
        ? ` <a href="/projects/${row.match.id}" target="_blank" rel="noopener" class="font-normal text-brand hover:underline">Open</a>` : ""
      match += `<div class="mt-1 font-medium text-gray-800">${this._esc(row.match.label)}${open}</div>
                ${row.match.detail ? `<div class="text-gray-500">${this._esc(row.match.detail)}</div>` : ""}`
    }
    if (row.reason) match += `<div class="text-gray-500 mt-0.5">${this._esc(row.reason)}</div>`
    if (row.adds_property) match += `<div class="text-emerald-700 mt-0.5">Has no property in ConsLog yet — filling in links it to ${this._esc(v.property_label || v.property_code || "this file's property")}</div>`
    if (row.ai_note) match += `<div class="text-purple-700 mt-0.5">${this._esc(row.ai_note)}</div>`
    const others = (row.candidates || []).slice(1)
    if (row.bucket === "possible" && others.length) {
      match += `<div class="text-gray-500 mt-0.5">Also similar: ${others.map((c) => this._esc(c.label)).join("; ")}</div>`
    }

    return `
      <tr class="align-top">
        <td class="px-2 py-1.5 text-gray-500 tabular-nums">${row.line}</td>
        <td class="px-2 py-1.5">
          <div class="font-medium text-gray-800">${this._esc(title)}</div>
          <div class="text-gray-500">${detail.filter(Boolean).map((part) => this._esc(part)).join(" · ")}</div>
          ${(row.warnings || []).map((w) => `<div class="text-amber-700 mt-0.5">${this._esc(w)}</div>`).join("")}
        </td>
        <td class="px-2 py-1.5">${match}</td>
        <td class="px-2 py-1.5">${this._decisionHtml(row)}</td>
      </tr>`
  }

  _decisionHtml(row) {
    const current = this.decisions[row.i] || "skip"
    if (row.bucket === "invalid") return `<span class="text-red-600">${this._esc(row.error || "Can't be imported")}</span>`

    if (row.bucket === "new") {
      return `<label class="inline-flex items-center gap-2 cursor-pointer">
                <input type="checkbox" class="rounded border-gray-300 text-brand focus:ring-brand" data-row="${row.i}" data-action="change->smart-import#toggleRow" ${current === "create" ? "checked" : ""}>
                <span class="text-gray-700">Import</span>
              </label>`
    }

    const labels = row.bucket === "matched"
      ? { skip: "Already in ConsLog — skip", fill: "Fill in its missing info" }
      : row.match
        ? { skip: "Same record — don't import", create: "Different — import as new", fill: "Same record — fill in its missing info" }
        : { skip: "Duplicate row — don't import", create: "Import it anyway" }

    return `<select data-row="${row.i}" data-action="change->smart-import#setDecision" class="w-full rounded-lg border border-gray-300 bg-white text-[13px] px-2 py-1">
              ${this._allowed(row).map((action) => `<option value="${action}" ${action === current ? "selected" : ""}>${labels[action]}</option>`).join("")}
            </select>`
  }

  _renderFooter() {
    if (this.step !== "review") return
    const counts = this._counts()
    const capacity = this.review.summary.capacity
    const overLimit = this.kindValue === "projects" && capacity && counts.create > capacity.remaining
    const nothing = counts.create + counts.fill === 0

    this.footerTarget.innerHTML = `
      <p class="text-[13px] text-gray-600 min-w-0">
        ${overLimit
          ? `<span class="text-red-600">This company's plan has room for ${capacity.remaining} more active project(s) (${capacity.used} of ${capacity.limit} used) — you've ticked ${counts.create}. Raise the limit in Admin → Companies, or untick some.</span>`
          : `Will create <span class="font-semibold text-gray-800">${counts.create}</span> &middot; fill in <span class="font-semibold text-gray-800">${counts.fill}</span> &middot; skip <span class="font-semibold text-gray-800">${counts.skip}</span>`}
      </p>
      <div class="flex items-center gap-2 shrink-0">
        <button type="button" class="px-4 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer" data-action="click->smart-import#hideModal">Close</button>
        <button type="button" class="px-4 py-1.5 text-sm font-medium text-white bg-brand hover:bg-brand-dark rounded-lg transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                data-action="click->smart-import#confirmImport" ${nothing || overLimit ? "disabled" : ""}>Confirm import</button>
      </div>`
  }

  _renderImporting() {
    const { done = 0, total = 0 } = this.progress || {}
    const percent = total > 0 ? Math.round((done / total) * 100) : 0
    this.bodyTarget.innerHTML = `
      <div class="px-5 py-12 text-center">
        <p class="text-sm font-medium text-gray-800">Importing… ${done} of ${total}</p>
        <div class="mt-3 h-2 rounded-full bg-gray-200 overflow-hidden max-w-md mx-auto">
          <div class="h-full bg-brand transition-all" style="width: ${percent}%"></div>
        </div>
        <p class="mt-3 text-[13px] text-gray-500">Keep this window open until it finishes.</p>
        ${this.error ? `
          <p class="mt-4 text-sm text-red-600">${this._esc(this.error)}</p>
          <button type="button" class="mt-2 px-4 py-1.5 text-sm font-medium text-white bg-brand hover:bg-brand-dark rounded-lg cursor-pointer transition-colors" data-action="click->smart-import#retryRun">Carry on</button>
          <p class="mt-2 text-[13px] text-gray-500">Rows already imported won't be imported twice.</p>` : ""}
      </div>`
    this.footerTarget.innerHTML = `<span></span><span></span>`
  }

  _renderHistory() {
    const what = this.kindValue === "projects" ? "projects" : "properties"
    const when = (iso) => iso ? new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : ""
    const list = this.history
    let body
    if (list === null) {
      body = `<p class="py-8 text-center text-sm text-gray-500">Loading…</p>`
    } else if (list.length === 0) {
      body = `<p class="py-8 text-center text-sm text-gray-500">No ${what} have been imported into this company yet.</p>`
    } else {
      body = `
        <div class="border border-gray-200 rounded-xl overflow-hidden">
          <table class="w-full text-[13px]">
            <thead class="bg-gray-50 text-gray-500 font-medium">
              <tr><th class="px-3 py-2 text-left">When</th><th class="px-3 py-2 text-left">File</th><th class="px-3 py-2 text-left">Result</th><th class="px-3 py-2 text-left w-40">Status</th></tr>
            </thead>
            <tbody class="divide-y divide-gray-200">
              ${list.map((h) => {
                const r = h.result || {}
                const result = [`${r.created || 0} created`, r.filled ? `${r.filled} filled in` : "", r.failed ? `${r.failed} failed` : ""].filter(Boolean).join(" · ")
                let status
                if (h.status === "reverted") {
                  const t = h.reverted?.tally || {}
                  status = `<span class="inline-block px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">Reverted</span>
                            <div class="text-gray-500 mt-0.5">${this._esc(when(h.reverted?.at))}${h.reverted?.by ? ` by ${this._esc(h.reverted.by)}` : ""}</div>
                            <div class="text-gray-500">${t.removed || 0} removed · ${t.cleared || 0} cleared · ${t.kept || 0} kept</div>`
                } else if (h.status === "importing" && !h.stalled) {
                  status = `<span class="inline-block px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">Running</span>`
                } else if (this.confirmRevert === h.id) {
                  status = `<div class="text-gray-800">Revert this import?</div>
                            <div class="text-gray-500 mt-0.5">Its new ${what} go to the trash${what === "properties" ? ", unless a project uses them" : ""}; details it filled in are cleared where nobody changed them since.</div>
                            <div class="mt-1.5 flex gap-1.5">
                              <button type="button" class="px-2.5 py-1 rounded-lg bg-red-600 hover:bg-red-700 text-white font-medium cursor-pointer" data-action="click->smart-import#doRevert" ${this.reverting ? "disabled" : ""}>${this.reverting ? "Reverting…" : "Yes, revert"}</button>
                              <button type="button" class="px-2.5 py-1 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 cursor-pointer" data-action="click->smart-import#cancelRevert">Cancel</button>
                            </div>`
                } else {
                  status = (h.status === "importing"
                    ? `<span class="inline-block px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">Stopped part way</span>
                       <div class="text-gray-500 mt-0.5">What it made is still in ConsLog.</div>`
                    : `<span class="inline-block px-2 py-0.5 rounded-full bg-green-100 text-green-700">Done</span>`) + `
                            <div class="mt-1.5"><button type="button" class="text-red-600 hover:underline cursor-pointer" data-action="click->smart-import#askRevert" data-id="${h.id}">Revert</button></div>`
                }
                return `<tr class="align-top">
                  <td class="px-3 py-2 text-gray-700 whitespace-nowrap">${this._esc(when(h.created_at))}<div class="text-gray-500">${this._esc(h.by || "")}</div></td>
                  <td class="px-3 py-2 text-gray-800">${this._esc(h.filename || "")}</td>
                  <td class="px-3 py-2 text-gray-700">${this._esc(result)}</td>
                  <td class="px-3 py-2">${status}</td>
                </tr>`
              }).join("")}
            </tbody>
          </table>
        </div>`
    }
    this.bodyTarget.innerHTML = `
      <div class="px-5 py-5">
        <p class="text-sm text-gray-600">Every import of ${what} into <span class="font-medium text-gray-800">${this._esc(this.companyValue)}</span>. A finished import can be reverted once; what it created goes to the trash, and what it filled in is cleared only where it is still exactly as imported.</p>
        ${this.error ? `<p class="mt-3 text-sm text-red-600">${this._esc(this.error)}</p>` : ""}
        <div class="mt-4">${body}</div>
      </div>`
    this.footerTarget.innerHTML = `
      <button type="button" class="px-4 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors cursor-pointer" data-action="click->smart-import#backToUpload">Back</button>
      <button type="button" class="px-4 py-1.5 text-sm font-medium text-white bg-brand hover:bg-brand-dark rounded-lg transition-colors cursor-pointer" data-action="click->smart-import#finish">Close</button>`
  }

  _renderDone() {
    const result = this.progress?.result || {}
    const failures = this.progress?.failures || []
    const stat = (count, label) => `<div class="rounded-lg border border-gray-200 px-3 py-2 text-center"><div class="text-lg font-semibold text-gray-900">${count || 0}</div><div class="text-[13px] text-gray-500">${label}</div></div>`

    this.bodyTarget.innerHTML = `
      <div class="px-5 py-6">
        <div class="flex items-center gap-2">
          <span class="inline-flex items-center justify-center w-7 h-7 rounded-full bg-green-100 text-green-700">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5"/></svg>
          </span>
          <p class="text-base font-semibold text-gray-900">Import finished</p>
        </div>
        <div class="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2">
          ${stat(result.created, "Created")}${stat(result.filled, "Filled in")}${stat(result.skipped, "Skipped")}${stat(result.failed, "Failed")}
        </div>
        ${failures.length ? `
          <p class="mt-4 text-sm font-medium text-gray-800">Rows that failed</p>
          <div class="mt-1 border border-gray-200 rounded-xl overflow-hidden max-h-56 overflow-y-auto">
            <table class="w-full text-[13px]">
              <tbody class="divide-y divide-gray-200">
                ${failures.map((f) => `<tr><td class="px-2 py-1.5 text-gray-500 w-16">Line ${f.line}</td><td class="px-2 py-1.5 text-gray-800">${this._esc(f.label)}</td><td class="px-2 py-1.5 text-red-600">${this._esc(f.error)}</td></tr>`).join("")}
              </tbody>
            </table>
          </div>` : ""}
      </div>`
    this.footerTarget.innerHTML = `
      <span></span>
      <button type="button" class="px-4 py-1.5 text-sm font-medium text-white bg-brand hover:bg-brand-dark rounded-lg transition-colors cursor-pointer" data-action="click->smart-import#finish">Done</button>`
  }

  // ── Helpers ─────────────────────────────────────────────────────────

  _money(value) {
    const number = Number(value)
    return Number.isFinite(number) ? number.toLocaleString(undefined, { maximumFractionDigits: 2 }) : String(value)
  }

  _esc(value) {
    const div = document.createElement("div")
    div.textContent = value == null ? "" : String(value)
    return div.innerHTML.replace(/"/g, "&quot;")
  }

  _showToast(message, type = "success") {
    const cssType = (type === "error" || type === "alert") ? "alert" : "notice"
    const icon = cssType === "notice"
      ? '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/></svg>'
      : '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/></svg>'
    const el = document.createElement("div")
    el.className = `flash-toast-${cssType}`
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.setAttribute("role", "alert")
    const iconDiv = document.createElement("div")
    iconDiv.className = "shrink-0"
    iconDiv.innerHTML = icon
    const msgP = document.createElement("p")
    msgP.className = "flex-1 text-sm"
    msgP.textContent = message
    el.appendChild(iconDiv)
    el.appendChild(msgP)
    document.body.prepend(el)
  }
}
